<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMapGetter, useStore, useStoreGetters } from 'dashboard/composables/store.js';
import { computed, nextTick, ref, inject } from 'vue';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import { watch } from 'vue';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
});

const showInput = ref(false);
const query = ref('');
const store = useStore();
const router = useRouter();
const route = useRoute();
const getters = useStoreGetters();

const conversationRecords = useMapGetter(
  'conversationSearch/getMessageConversationRecords',
);

const messages = computed(() => conversationRecords.value?.messages || []);

const currentMessage = computed(() => {
  if (!messages.value.length) return null;

  // globalIndex empieza en 1 si hay mensajes
  const index = globalIndex.value > 0 ? globalIndex.value - 1 : 0;

  return messages.value[index] || null;
});

const totalCount = computed(() => conversationRecords.value?.total_count || 0);
const totalPages = computed(() => conversationRecords.value?.total_pages || 0);
const currentPage = computed(() => conversationRecords.value?.current_page || 1);
const globalIndex = ref(0);
const isFirst = computed(() => globalIndex.value <= 1 || totalCount.value === 0);
const isLast = computed(() => globalIndex.value >= totalCount.value || totalCount.value === 0);
const PER_PAGE = 15;
// Inject the scroll container ref provided by MessagesView
const conversationPanelRef = inject('contextMenuElementTarget', ref(null));
const currentChat = computed(() => getters.getSelectedChat.value);
const listLoadingStatus = computed(() => getters.getAllMessagesLoaded.value);
const isLoadingPrevious = ref(false);
const searchRef = ref(null);
// Helper to know if we can load more messages from the server
function canLoadMoreMessages() {
  return (
    currentChat.value?.dataFetched === true &&
    !listLoadingStatus.value &&
    !isLoadingPrevious.value
  );
}

// Resolve the actual scrollable element (component -> $el or direct element)
function getPanelEl() {
  const refVal = conversationPanelRef?.value ?? null;
  // If it's a Vue component, use $el; if it's already an element, use it; fallback to local ref
  const el = (refVal && '$el' in refVal) ? refVal.$el : refVal;
  // Fallback: try to locate by class used in MessagesView
  return (
    el ??
    conversationPanel.value ??
    (typeof document !== 'undefined'
      ? document.querySelector('.conversation-panel')
      : null)
  );
}

// Scroll the panel so that the message element is centered
function scrollMessageIntoPanelView(messageEl) {
  const panel = getPanelEl();
  if (!panel || !messageEl) return;

  const panelRect = panel.getBoundingClientRect();
  const msgRect = messageEl.getBoundingClientRect();
  const currentScroll = panel.scrollTop;
  const offsetTop = msgRect.top - panelRect.top + currentScroll;
  const desiredTop = Math.max(0, offsetTop - panel.clientHeight / 2 + messageEl.clientHeight / 2);

  if (typeof panel.scrollTo === 'function') {
    panel.scrollTo({ top: desiredTop, behavior: 'smooth' });
  } else {
    panel.scrollTop = desiredTop;
  }
}

const toggleInput = () => {
  showInput.value = !showInput.value;
};

watch(totalCount, (newCount) => {
  if (newCount > 0) {
    globalIndex.value = 1;
  } else {
    globalIndex.value = 0;
  }
}, { immediate: true });

const onSearch = async () => {
  if (!query.value) return;
  useTrack(CONVERSATION_EVENTS.SEARCH_CONVERSATION);
  await store.dispatch('conversationSearch/messagesChatSearch', { conversation: props.conversationId  ,q: query.value , page: 1 });

};
const conversationPanel = ref(null);
let heightBeforeLoad = 0;
let scrollTopBeforeLoad = 0;

function setScrollParams() {
  const panel = getPanelEl();
  if (panel) {
    heightBeforeLoad = panel.scrollHeight;
    scrollTopBeforeLoad = panel.scrollTop;
  }
}

async function fetchPreviousMessages(scrollTop = 0) {
  setScrollParams();

  const shouldLoadMoreMessages = canLoadMoreMessages();

  // Only load more if we're close to the top and loading is allowed
  if (scrollTop < 100 && shouldLoadMoreMessages) {
    isLoadingPrevious.value = true;
    try {
      await store.dispatch('fetchPreviousMessages', {
        conversationId: currentChat.value.id,
        before: currentChat.value.messages[0].id,
      });

      const panel = getPanelEl();
      if (panel) {
        const heightDifference = panel.scrollHeight - heightBeforeLoad;
        panel.scrollTop = scrollTopBeforeLoad + heightDifference;
        setScrollParams();
      }
    } catch (error) {
      // ignorar error
    } finally {
      isLoadingPrevious.value = false;
    }
  }
}
const onScrollToMessage = async ({ messageId = '' }) => {
  let messageElement = document.getElementById('message' + messageId);

  // Safety cap to avoid infinite loops due to unexpected states
  let attempts = 0;
  const MAX_ATTEMPTS = 50;

  while (!messageElement && attempts < MAX_ATTEMPTS) {
    attempts += 1;

    const panel = getPanelEl();
    const currentTop = panel?.scrollTop ?? Number.POSITIVE_INFINITY;

    // If we cannot load more messages anymore, break to avoid infinite loop
    if (!canLoadMoreMessages()) {
      break;
    }

    // Scroll up a bit to trigger top-loading behavior, only if panel exists
    let newTop = currentTop;
    if (panel && Number.isFinite(currentTop) && currentTop > 0) {
      newTop = Math.max(0, currentTop - 200);
      panel.scrollTop = newTop;
    }

    // Try loading the previous page using the updated scrollTop
    await fetchPreviousMessages(Number.isFinite(newTop) ? newTop : 9999);
    await nextTick();
    messageElement = document.getElementById('message' + messageId);

    // If we reached the top of the panel, stop trying further
    if (panel && panel.scrollTop === 0) break;
  }

  if (messageElement) {
    await nextTick();
    scrollMessageIntoPanelView(messageElement);
    // Parpadeo
    messageElement.classList.add('highlight');
    setTimeout(() => messageElement.classList.remove('highlight'), 2000);
  } else {
    // Si no encontró nada, ir al top
    const panel = getPanelEl();
    if (panel) panel.scrollTop = 0;
  }

};
watch(currentMessage, (msg) => {
  if (!msg) return;
  onScrollToMessage({ messageId: msg.id });
  router.replace({
    query: {
      ...route.query,
      messageId: msg.id, // el ID del mensaje actual
    },
  });

}, { immediate: true });
const goNext = async () => {
  if (isLast.value) return;
  globalIndex.value++;
  // si pasamos de página, pedimos la siguiente
  const page = Math.ceil(globalIndex.value / PER_PAGE);
  if (page !== currentPage.value) {
    await store.dispatch('conversationSearch/messagesChatSearch', {
      conversation: props.conversationId,
      q: query.value,
      page,
    });
  }
};

const goPrev = async () => {
  if (isFirst.value) return;
  globalIndex.value--;

  // si pasamos de página hacia atrás
  const page = Math.ceil(globalIndex.value / PER_PAGE);
  if (page !== currentPage.value) {
    await store.dispatch('conversationSearch/messagesChatSearch', {
      conversation: props.conversationId,
      q: query.value,
      page,
    });
  }
};

const handleClickOutside = () => {
  showInput.value = false;
  query.value = '';
  globalIndex.value = 0;
  store.commit('conversationSearch/CLEAR_SEARCH_RESULTS');
};

onClickOutside(searchRef, handleClickOutside);

</script>
<template>
  <div class="flex items-center">
    <Button @click="toggleInput"
     class=" !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11 inline-flex items-center min-w-0 gap-2
     transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-solid-3
     dark:hover:enabled:bg-n-solid-2 dark:focus-visible:bg-n-solid-2 hover:enabled:bg-n-alpha-2
     focus-visible:bg-n-alpha-2 text-n-slate-12 outline-n-container h-8 w-8 !p-0 text-sm justify-center">
      <Icon icon="i-lucide-search" class="text-dark" />
    </Button>
   <div
      v-if="showInput"
      class="absolute z-10 top-[40px] right-0 w-72
             dark:bg-gray-800
             outline outline-1 outline-n-weak bg-n-solid-3
            rounded-md shadow-lg p-2"
      >
      <div class="flex items-center gap-2 w-full" ref="searchRef">
        <!-- Input con ícono de lupa -->
        <div class="relative flex-1">
          <input
            v-model="query"
            type="text"
            placeholder="Buscar"
            class="w-full pl-8 pr-2 py-1 rounded-md border
             rounded-lg h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30
                  my-0 !mb-0 "
            @keydown.enter="onSearch"
          />
        </div>

        <div
          class="px-3 py-1 text-sm text-n-slate-12
                text-gray-600 dark:text-gray-300 select-none"
        >
          {{ totalCount === 0 ? '0/0' : `${globalIndex} / ${totalCount}` }}
        </div>

        <!-- Botones de navegación -->
        <div class="flex flex-col rounded-md">
          <button
            type="button"
            class="px-2 py-0
                  text-gray-600 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-gray-600"
            @click="goNext"
          >
            <Icon icon="i-lucide-chevron-up" />
          </button>
          <button
            type="button"
            class="px-2 py-0
                  text-gray-600 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-gray-600"
            @click="goPrev"
          >
            <Icon icon="i-lucide-chevron-down" />
          </button>
        </div>
      </div>
    </div>

  </div>
</template>
<style>
.message {
  padding: 8px;
  border-bottom: 1px solid #ccc;
  transition: background-color 0.3s ease;
}

.highlight {
  background-color: #f4f4f4;
  animation: blink 0.5s alternate 4; /* parpadea 4 veces */
}

@keyframes blink {
  from { background-color: #f4f4f4; }
  to { background-color: transparent; }
}
</style>
